import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import './Auth.css';

export const ForgotPassword: React.FC = () => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  // #45 fix (2026-05-17): user-enumeration via password-reset error message.
  // Previously responded "No account found with this email" — revealed which
  // emails are registered (OWASP user-enumeration class). Now we show the
  // SAME generic confirmation regardless of whether the email exists, even
  // when Firebase returns `auth/user-not-found`. Also enable Firebase's
  // built-in Email Enumeration Protection in the console:
  //   Authentication → Settings → User account linking →
  //     "Email enumeration protection" ON
  // (which makes Firebase itself stop distinguishing the cases server-side).
  const handlePasswordReset = async () => {
    setIsLoading(true);
    setSubmitError('');
    setSuccessMessage('');

    // The generic message we show for ALL success and the "not found" case.
    const GENERIC_MSG =
      'If that email is registered, you will receive a password-reset link shortly. Check your inbox (and spam folder).';

    try {
      await resetPassword(email);
      setSuccessMessage(GENERIC_MSG);
    } catch (error) {
      const errorCode = error && typeof error === 'object' && 'code' in error
        ? (error as { code?: string }).code
        : undefined;
      // Critical: do NOT differentiate "user not found" from "sent" in the UI.
      if (errorCode === 'auth/user-not-found') {
        setSuccessMessage(GENERIC_MSG);
        return;
      }

      let errorMessage = 'Failed to send password reset email. Please try again.';
      switch (errorCode) {
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection and try again.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many attempts. Please try again later.';
          break;
        default:
          if (error instanceof Error && error.message) {
            errorMessage = error.message;
          }
      }

      setSubmitError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email
    setEmailError('');
    setSubmitError('');

    if (!email) {
      setEmailError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    handlePasswordReset();
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Reset Your Password</h1>
        <p className="auth-subtitle">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {successMessage && (
          <div className="auth-success" role="status">{successMessage}</div>
        )}

        {submitError && (
          <div className="auth-error" role="alert">{submitError}</div>
        )}

        <form onSubmit={onSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
                if (submitError) setSubmitError('');
              }}
              required
              placeholder="Enter your email"
              className={emailError ? 'error' : ''}
              aria-describedby={emailError ? 'email-error' : undefined}
              aria-invalid={!!emailError}
            />
            {emailError && (
              <div id="email-error" className="field-error" role="alert">
                {emailError}
              </div>
            )}
          </div>

          <button type="submit" className="auth-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner size="small" />
                Sending Reset Email...
              </>
            ) : (
              'Send Reset Email'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
